/**
 * Self-hosted Umami analytics — single entry point for client events.
 * Do not call window.umami from components; use these helpers instead.
 */

type UmamiPrimitive = string | number | boolean

export type AcquisitionSourceKind = "organic" | "social" | "referral" | "direct"

declare global {
  interface Window {
    umami?:
      | ((eventName?: string, data?: Record<string, UmamiPrimitive>) => void)
      | { track?: (eventName: string, data?: Record<string, UmamiPrimitive>) => void }
  }
}

/** Internal: forwards to Umami when the script loaded; no-op otherwise. */
function sendUmami(eventName: string, data?: Record<string, UmamiPrimitive>) {
  if (typeof window === "undefined") return
  const u = window.umami
  if (!u) return
  if (typeof u === "function") {
    u(eventName, data)
    return
  }
  if (typeof u.track === "function") {
    u.track(eventName, data)
  }
}

function stripWww(hostname: string) {
  return hostname.toLowerCase().replace(/^www\./, "")
}

const SOCIAL_SUFFIXES = [
  "twitter.com",
  "t.co",
  "x.com",
  "facebook.com",
  "instagram.com",
  "linkedin.com",
  "lnkd.in",
  "reddit.com",
  "pinterest.com",
  "tiktok.com",
  "youtube.com",
]

function hostIsSocial(hostNorm: string): boolean {
  return SOCIAL_SUFFIXES.some((s) => hostNorm === s || hostNorm.endsWith(`.${s}`))
}

function hostIsOrganic(hostNorm: string): boolean {
  if (hostNorm === "google.com" || hostNorm.endsWith(".google.com")) return true
  if (hostNorm.includes(".google.") || hostNorm.startsWith("google.")) return true
  if (hostNorm.endsWith("bing.com") || hostNorm === "bing.com") return true
  if (hostNorm.endsWith("duckduckgo.com") || hostNorm === "duckduckgo.com") return true
  if (hostNorm.includes("yahoo.com")) return true
  if (hostNorm === "ecosia.org" || hostNorm.endsWith(".ecosia.org")) return true
  if (hostNorm.endsWith("search.brave.com") || hostNorm === "search.brave.com") return true
  return false
}

/**
 * Classify external referrer for acquisition events (no PII).
 */
export function classifyReferrer(hostname: string): AcquisitionSourceKind {
  const host = stripWww(hostname)
  if (!host) return "direct"

  if (hostIsOrganic(host)) return "organic"
  if (hostIsSocial(host)) return "social"
  return "referral"
}

export function trackScrollDepth(payload: { depth: "25%" | "50%" | "75%" | "100%"; book_slug: string }) {
  sendUmami("scroll_depth", {
    depth: payload.depth,
    book_slug: payload.book_slug,
  })
}

export type PodcastPlayAction = "play" | "paused" | "25%" | "50%" | "75%" | "completed"

export function trackPodcastPlay(payload: {
  action: PodcastPlayAction
  episode_id: string
  episode_title: string
  book_slug: string
}) {
  sendUmami("podcast_play", {
    action: payload.action,
    episode_id: payload.episode_id,
    episode_title: payload.episode_title,
    book_slug: payload.book_slug,
  })
}

export function trackAcquisition(payload: { source: AcquisitionSourceKind; referrer_domain: string }) {
  sendUmami("acquisition", {
    source: payload.source,
    referrer_domain: payload.referrer_domain,
  })
}

export function trackCtaConversion(payload: {
  type: "newsletter_signup" | "cta_click"
  location: string
}) {
  sendUmami("cta_conversion", {
    type: payload.type,
    location: payload.location,
  })
}

export function trackSearch(payload: { query: string; results_count: number }) {
  sendUmami("search", {
    query: payload.query.slice(0, 200),
    results_count: payload.results_count,
  })
}

export function trackCollectionView(payload: {
  collection_name: string
  collection_slug: string
  book_count: number
}) {
  sendUmami("collection_view", {
    collection_name: payload.collection_name.slice(0, 200),
    collection_slug: payload.collection_slug,
    book_count: payload.book_count,
  })
}
